
import createRandomString from '../utils/createRandomString'

export type ResponseResult = Record<string, any> | any[] | string | number | boolean | null
export type ResponseError = Record<string, any> | string | Error

export interface HttpClientParams {
  onSuccess?: (res?: ResponseResult) => void
  onError?: (err: ResponseError) => void
  onProgress?: (progress: number) => void
}

export interface SendRequestParams {
  url: string
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  body?: string | FormData | Object
  contentType?:
    | 'application/x-www-form-urlencoded'
    | 'application/json'
    | 'text/plain'
    | 'multipart/form-data'
  useConsoleError?: boolean
  onSuccess?: (res?: ResponseResult) => void
  onError?: (err: ResponseError) => void
  onProgress?: (progress: number) => void
  isAsynchronousRequest?: boolean
}

interface SetupListenersParams extends SendRequestParams {
  xhr: XMLHttpRequest
}

const LENGTH_REQUEST_ID = 12

class HttpClient {
  static onSuccess?: (res?: ResponseResult) => void
  static onError?: (err: ResponseError) => void
  static onProgress?: (progress: number) => void

  private constructor() {}

  static configure({ onSuccess, onError, onProgress }: HttpClientParams = {}): void {
    HttpClient.onSuccess = onSuccess
    HttpClient.onError = onError
    HttpClient.onProgress = onProgress
  }

  private static prepareGetUrl(url: string, body: any): string {
    if (!body) return url
    const queryString = typeof body === 'string' 
      ? body 
      : new URLSearchParams(body as Record<string, string>).toString()
    
    if (!queryString) return url
    const separator = url.includes('?') ? '&' : '?'
    return `${url}${separator}${queryString}`
  }

  private static preparePayload(body: any, contentType: string): any {
    if (body instanceof FormData || typeof body === 'string') {
      return body
    }
    if (contentType === 'application/x-www-form-urlencoded') {
      return new URLSearchParams(body as Record<string, string>).toString()
    }
    return JSON.stringify(body)
  }

  private static processTextResponse(text: string): any {
    try {
      return text ? JSON.parse(text) : null
    } catch {
      return text
    }
  }

  private static setupXhrListeners({
    xhr,
    method,
    useConsoleError,
    onSuccess,
    onError,
    onProgress,
  }: SetupListenersParams): void {
    const resolveRequest = (res: ResponseResult): void => {
      onSuccess?.(res)
      HttpClient.onSuccess?.(res)
    }

    const rejectRequest = (err: ResponseError): void => {
      onError?.(err)
      HttpClient.onError?.(err)
      if (useConsoleError !== false) {
        console.error(err)
      }
    }

    const updateProgress = (event: ProgressEvent): void => {
      if (event.lengthComputable) {
        const percentage = Math.round((event.loaded / event.total) * 100)
        onProgress?.(percentage)
        HttpClient.onProgress?.(percentage)
      }
    }

    if (method === 'GET') {
      xhr.onprogress = updateProgress
    } else {
      xhr.upload.onprogress = updateProgress
    }

    xhr.onload = (): void => {
      const isSuccess = xhr.status >= 200 && xhr.status < 300
      const res = HttpClient.processTextResponse(xhr.responseText)

      if (isSuccess) {
        resolveRequest(res)
      } else {
        rejectRequest(res || new Error(`Request failed with status ${xhr.status}`))
      }
    }

    xhr.onerror = () => rejectRequest(new Error('Network error'))
    xhr.onabort = () => rejectRequest(new Error('Request aborted'))
  }

  static sendRequest = (params: SendRequestParams): XMLHttpRequest => {
    const {
      url,
      method = 'POST',
      body = '',
      contentType = 'application/json',
      isAsynchronousRequest = true,
    } = params

    const isGet = method === 'GET'
    const finalUrl = isGet ? HttpClient.prepareGetUrl(url, body) : url
    const requestPayload = isGet ? null : HttpClient.preparePayload(body, contentType)

    const xhr = new XMLHttpRequest()
    xhr.open(method, finalUrl, isAsynchronousRequest)
    
    if (contentType !== 'multipart/form-data') {
      xhr.setRequestHeader('Content-type', contentType)
    }
    xhr.setRequestHeader('x-requestid', `crid_${createRandomString(LENGTH_REQUEST_ID)}`)
    
    HttpClient.setupXhrListeners({ ...params, method, xhr })
    xhr.send(requestPayload)

    return xhr
  }
}

export default HttpClient

export const sendRequest = (params: SendRequestParams): XMLHttpRequest => HttpClient.sendRequest(params)
